/**
 * Un petit drapeau (badge en bas à gauche de l'icône d'une vente, la langue
 * d'un fan sous une bulle traduite).
 *
 * Pas d'emoji : Windows n'a pas les drapeaux emoji (il affiche « FR » en
 * lettres), et le cockpit vit sur Windows. On dessine les drapeaux en SVG —
 * des bandes pour la plupart, quelques dessins à part (croix nordiques, étoiles,
 * croissants) — et pour les autres on écrit le code pays sur un fond sombre :
 * lisible partout, jamais cassé.
 *
 * 02-11-2026 (vague 2) : les trente-quatre langues que Jev lit ont toutes leur
 * pays dessiné (`PAYS_DESSINES`, lu par `DrapeauDeLangue`) : avant, une
 * vingtaine (grec, hébreu, ukrainien, roumain, tchèque…) tombaient en lettres.
 */
import { cn } from "../utils";

type Bandes = { sens: "v" | "h"; couleurs: string[] };

const BANDES: Record<string, Bandes> = {
  FR: { sens: "v", couleurs: ["#0055A4", "#FFFFFF", "#EF4135"] },
  BE: { sens: "v", couleurs: ["#000000", "#FAE042", "#ED2939"] },
  IT: { sens: "v", couleurs: ["#009246", "#FFFFFF", "#CE2B37"] },
  IE: { sens: "v", couleurs: ["#169B62", "#FFFFFF", "#FF883E"] },
  RO: { sens: "v", couleurs: ["#002B7F", "#FCD116", "#CE1126"] },
  DE: { sens: "h", couleurs: ["#000000", "#DD0000", "#FFCE00"] },
  NL: { sens: "h", couleurs: ["#AE1C28", "#FFFFFF", "#21468B"] },
  LU: { sens: "h", couleurs: ["#ED2939", "#FFFFFF", "#00A1DE"] },
  AT: { sens: "h", couleurs: ["#ED2939", "#FFFFFF", "#ED2939"] },
  PL: { sens: "h", couleurs: ["#FFFFFF", "#DC143C"] },
  ES: { sens: "h", couleurs: ["#AA151B", "#F1BF00", "#F1BF00", "#AA151B"] },
  CO: { sens: "h", couleurs: ["#FCD116", "#FCD116", "#003893", "#CE1126"] },
  PT: { sens: "v", couleurs: ["#006600", "#006600", "#FF0000", "#FF0000", "#FF0000"] },
  RU: { sens: "h", couleurs: ["#FFFFFF", "#0039A6", "#D52B1E"] },
  UA: { sens: "h", couleurs: ["#0057B7", "#FFD700"] },
  HU: { sens: "h", couleurs: ["#CE2939", "#FFFFFF", "#477050"] },
  BG: { sens: "h", couleurs: ["#FFFFFF", "#00966E", "#D62612"] },
  ID: { sens: "h", couleurs: ["#CE1126", "#FFFFFF"] },
  TH: { sens: "h", couleurs: ["#A51931", "#FFFFFF", "#2D2A4A", "#2D2A4A", "#FFFFFF", "#A51931"] },
};

function Bandes({ sens, couleurs }: Bandes) {
  const n = couleurs.length;
  return (
    <svg viewBox="0 0 12 9" width="100%" height="100%" aria-hidden>
      {couleurs.map((c, i) =>
        sens === "v" ? (
          <rect key={i} x={(12 / n) * i} y={0} width={12 / n + 0.02} height={9} fill={c} />
        ) : (
          <rect key={i} x={0} y={(9 / n) * i} width={12} height={9 / n + 0.02} fill={c} />
        ),
      )}
    </svg>
  );
}

/** Une croix nordique : le champ, la croix (décalée vers la hampe), et pour la Norvège la croix dedans. */
function Nordique({ fond, croix, dedans, large = 1.8 }: { fond: string; croix: string; dedans?: string; large?: number }) {
  const x = 3.4;
  const y = 4.5 - large / 2;
  return (
    <svg viewBox="0 0 12 9" width="100%" height="100%" aria-hidden>
      <rect width={12} height={9} fill={fond} />
      <rect x={x} y={0} width={large} height={9} fill={croix} />
      <rect x={0} y={y} width={12} height={large} fill={croix} />
      {dedans && (
        <>
          <rect x={x + large / 4} y={0} width={large / 2} height={9} fill={dedans} />
          <rect x={0} y={y + large / 4} width={12} height={large / 2} fill={dedans} />
        </>
      )}
    </svg>
  );
}

/** Une étoile à cinq branches, pleine. */
function Etoile({ cx, cy, r, fill }: { cx: number; cy: number; r: number; fill: string }) {
  const points: string[] = [];
  for (let i = 0; i < 10; i++) {
    const rayon = i % 2 === 0 ? r : r * 0.4;
    const angle = (-90 + i * 36) * (Math.PI / 180);
    points.push(`${(cx + rayon * Math.cos(angle)).toFixed(2)} ${(cy + rayon * Math.sin(angle)).toFixed(2)}`);
  }
  return <path d={`M${points.join(" L")} Z`} fill={fill} />;
}

/** Les drapeaux qui ne sont pas des bandes. `PAYS_SPECIAUX` doit en citer chaque `case`. */
const PAYS_SPECIAUX = ["US", "GB", "CA", "CH", "BR", "JP", "MA", "SK", "HR", "IN", "SE", "FI", "DK", "NO", "GR", "IL", "TR", "CN", "KR", "SA", "VN", "IR", "MY", "PH", "CZ"] as const;

function Special({ pays }: { pays: string }) {
  switch (pays) {
    case "US":
      return (
        <svg viewBox="0 0 12 9" width="100%" height="100%" aria-hidden>
          {Array.from({ length: 7 }, (_, i) => (
            <rect key={i} x={0} y={i * (9 / 7)} width={12} height={9 / 7 + 0.02} fill={i % 2 === 0 ? "#B22234" : "#FFFFFF"} />
          ))}
          <rect x={0} y={0} width={5.2} height={9 * (4 / 7)} fill="#3C3B6E" />
        </svg>
      );
    case "GB":
      return (
        <svg viewBox="0 0 12 9" width="100%" height="100%" aria-hidden>
          <rect width={12} height={9} fill="#012169" />
          <path d="M0 0 L12 9 M12 0 L0 9" stroke="#FFFFFF" strokeWidth={1.6} />
          <path d="M0 0 L12 9 M12 0 L0 9" stroke="#C8102E" strokeWidth={0.6} />
          <path d="M6 0 V9 M0 4.5 H12" stroke="#FFFFFF" strokeWidth={2.4} />
          <path d="M6 0 V9 M0 4.5 H12" stroke="#C8102E" strokeWidth={1.3} />
        </svg>
      );
    case "CA":
      return (
        <svg viewBox="0 0 12 9" width="100%" height="100%" aria-hidden>
          <rect width={12} height={9} fill="#FFFFFF" />
          <rect width={3} height={9} fill="#D80621" />
          <rect x={9} width={3} height={9} fill="#D80621" />
          <path d="M6 2.2 L6.6 3.4 L7.5 3.1 L7.1 4.4 L7.9 4.6 L6.4 5.6 L6.5 6.6 L6 6.4 L5.5 6.6 L5.6 5.6 L4.1 4.6 L4.9 4.4 L4.5 3.1 L5.4 3.4 Z" fill="#D80621" />
        </svg>
      );
    case "CH":
      return (
        <svg viewBox="0 0 12 9" width="100%" height="100%" aria-hidden>
          <rect width={12} height={9} fill="#DA291C" />
          <path d="M6 2 V7 M3.5 4.5 H8.5" stroke="#FFFFFF" strokeWidth={1.5} />
        </svg>
      );
    case "BR":
      return (
        <svg viewBox="0 0 12 9" width="100%" height="100%" aria-hidden>
          <rect width={12} height={9} fill="#009C3B" />
          <path d="M6 1 L11 4.5 L6 8 L1 4.5 Z" fill="#FFDF00" />
          <circle cx={6} cy={4.5} r={1.9} fill="#002776" />
        </svg>
      );
    case "JP":
      return (
        <svg viewBox="0 0 12 9" width="100%" height="100%" aria-hidden>
          <rect width={12} height={9} fill="#FFFFFF" />
          <circle cx={6} cy={4.5} r={2.6} fill="#BC002D" />
        </svg>
      );
    case "MA":
      return (
        <svg viewBox="0 0 12 9" width="100%" height="100%" aria-hidden>
          <rect width={12} height={9} fill="#C1272D" />
          <path d="M6 2.4 L6.7 4.4 L8.8 4.4 L7.1 5.6 L7.7 7.6 L6 6.4 L4.3 7.6 L4.9 5.6 L3.2 4.4 L5.3 4.4 Z" fill="none" stroke="#006233" strokeWidth={0.55} />
        </svg>
      );
    case "SK":
      // Blanc, bleu, rouge — comme la Russie : c'est l'écu à la double croix qui le dit.
      return (
        <svg viewBox="0 0 12 9" width="100%" height="100%" aria-hidden>
          <rect width={12} height={9} fill="#FFFFFF" />
          <rect y={3} width={12} height={3.02} fill="#0B4EA2" />
          <rect y={6} width={12} height={3} fill="#EE1C25" />
          <path d="M2.6 2.2 H5.4 V5.3 Q5.4 6.6 4 7.2 Q2.6 6.6 2.6 5.3 Z" fill="#EE1C25" stroke="#FFFFFF" strokeWidth={0.3} />
          <path d="M4 2.9 V6.4 M3.2 3.8 H4.8 M3.4 4.7 H4.6" stroke="#FFFFFF" strokeWidth={0.4} />
        </svg>
      );
    case "HR":
      // Rouge, blanc, bleu — comme les Pays-Bas : c'est le damier qui le dit.
      return (
        <svg viewBox="0 0 12 9" width="100%" height="100%" aria-hidden>
          <rect width={12} height={9} fill="#FF0000" />
          <rect y={3} width={12} height={3.02} fill="#FFFFFF" />
          <rect y={6} width={12} height={3} fill="#171796" />
          {Array.from({ length: 16 }, (_, i) => (
            <rect
              key={i}
              x={4.4 + (i % 4) * 0.8}
              y={2.9 + Math.floor(i / 4) * 0.8}
              width={0.82}
              height={0.82}
              fill={(i + Math.floor(i / 4)) % 2 === 0 ? "#FF0000" : "#FFFFFF"}
            />
          ))}
        </svg>
      );
    case "IN":
      return (
        <svg viewBox="0 0 12 9" width="100%" height="100%" aria-hidden>
          <rect width={12} height={9} fill="#FF9933" />
          <rect y={3} width={12} height={3.02} fill="#FFFFFF" />
          <rect y={6} width={12} height={3} fill="#138808" />
          <circle cx={6} cy={4.5} r={1.15} fill="none" stroke="#000080" strokeWidth={0.3} />
          <circle cx={6} cy={4.5} r={0.25} fill="#000080" />
        </svg>
      );
    case "SE":
      return <Nordique fond="#006AA7" croix="#FECC00" />;
    case "FI":
      return <Nordique fond="#FFFFFF" croix="#003580" large={2} />;
    case "DK":
      return <Nordique fond="#C8102E" croix="#FFFFFF" large={1.6} />;
    case "NO":
      return <Nordique fond="#BA0C2F" croix="#FFFFFF" dedans="#00205B" large={2.4} />;
    case "GR":
      return (
        <svg viewBox="0 0 12 9" width="100%" height="100%" aria-hidden>
          {Array.from({ length: 9 }, (_, i) => (
            <rect key={i} y={i} width={12} height={1.02} fill={i % 2 === 0 ? "#0D5EAF" : "#FFFFFF"} />
          ))}
          <rect width={5} height={5} fill="#0D5EAF" />
          <rect x={2} width={1} height={5} fill="#FFFFFF" />
          <rect y={2} width={5} height={1} fill="#FFFFFF" />
        </svg>
      );
    case "IL":
      return (
        <svg viewBox="0 0 12 9" width="100%" height="100%" aria-hidden>
          <rect width={12} height={9} fill="#FFFFFF" />
          <rect y={1} width={12} height={1.2} fill="#0038B8" />
          <rect y={6.8} width={12} height={1.2} fill="#0038B8" />
          <path d="M6 2.7 L7.4 5.1 L4.6 5.1 Z M6 6.3 L4.6 3.9 L7.4 3.9 Z" fill="none" stroke="#0038B8" strokeWidth={0.4} />
        </svg>
      );
    case "TR":
      return (
        <svg viewBox="0 0 12 9" width="100%" height="100%" aria-hidden>
          <rect width={12} height={9} fill="#E30A17" />
          <circle cx={4.5} cy={4.5} r={2.2} fill="#FFFFFF" />
          <circle cx={5.1} cy={4.5} r={1.75} fill="#E30A17" />
          <Etoile cx={7.5} cy={4.5} r={1} fill="#FFFFFF" />
        </svg>
      );
    case "CN":
      return (
        <svg viewBox="0 0 12 9" width="100%" height="100%" aria-hidden>
          <rect width={12} height={9} fill="#DE2910" />
          <Etoile cx={2.4} cy={2.5} r={1.4} fill="#FFDE00" />
          {[
            [4.7, 1.1],
            [5.4, 2.1],
            [5.4, 3.2],
            [4.7, 4.1],
          ].map(([x, y]) => (
            <circle key={`${x}-${y}`} cx={x} cy={y} r={0.32} fill="#FFDE00" />
          ))}
        </svg>
      );
    case "KR":
      return (
        <svg viewBox="0 0 12 9" width="100%" height="100%" aria-hidden>
          <rect width={12} height={9} fill="#FFFFFF" />
          <path d="M4 4.5 A2 2 0 0 1 8 4.5 Z" fill="#CD2E3A" />
          <path d="M4 4.5 A2 2 0 0 0 8 4.5 Z" fill="#0047A0" />
          <path d="M1.4 2.3 L2.9 1.1 M1.7 2.8 L3.2 1.6 M8.8 1.1 L10.3 2.3 M8.5 1.6 L10 2.8 M1.4 6.7 L2.9 7.9 M1.7 6.2 L3.2 7.4 M8.8 7.9 L10.3 6.7 M8.5 7.4 L10 6.2" stroke="#000000" strokeWidth={0.35} />
        </svg>
      );
    case "SA":
      return (
        <svg viewBox="0 0 12 9" width="100%" height="100%" aria-hidden>
          <rect width={12} height={9} fill="#006C35" />
          <path d="M2.6 3.4 q0.9 -0.9 1.8 0 t1.8 0 t1.8 0 t1.4 0" fill="none" stroke="#FFFFFF" strokeWidth={0.5} />
          <path d="M2.6 6.2 H9.2" stroke="#FFFFFF" strokeWidth={0.6} />
        </svg>
      );
    case "VN":
      return (
        <svg viewBox="0 0 12 9" width="100%" height="100%" aria-hidden>
          <rect width={12} height={9} fill="#DA251D" />
          <Etoile cx={6} cy={4.6} r={2.3} fill="#FFFF00" />
        </svg>
      );
    case "IR":
      return (
        <svg viewBox="0 0 12 9" width="100%" height="100%" aria-hidden>
          <rect width={12} height={9} fill="#239F40" />
          <rect y={3} width={12} height={3.02} fill="#FFFFFF" />
          <rect y={6} width={12} height={3} fill="#DA0000" />
          <path d="M6 3.4 Q7.2 4.5 6 5.6 Q4.8 4.5 6 3.4 Z" fill="none" stroke="#DA0000" strokeWidth={0.35} />
        </svg>
      );
    case "MY":
      return (
        <svg viewBox="0 0 12 9" width="100%" height="100%" aria-hidden>
          {Array.from({ length: 14 }, (_, i) => (
            <rect key={i} y={i * (9 / 14)} width={12} height={9 / 14 + 0.02} fill={i % 2 === 0 ? "#CC0001" : "#FFFFFF"} />
          ))}
          <rect width={6} height={4.5} fill="#010066" />
          <circle cx={2.4} cy={2.25} r={1.4} fill="#FFCC00" />
          <circle cx={2.95} cy={2.25} r={1.2} fill="#010066" />
          <Etoile cx={4.4} cy={2.25} r={0.75} fill="#FFCC00" />
        </svg>
      );
    case "PH":
      return (
        <svg viewBox="0 0 12 9" width="100%" height="100%" aria-hidden>
          <rect width={12} height={4.5} fill="#0038A8" />
          <rect y={4.5} width={12} height={4.5} fill="#CE1126" />
          <path d="M0 0 L5.2 4.5 L0 9 Z" fill="#FFFFFF" />
          <circle cx={1.8} cy={4.5} r={0.8} fill="#FCD116" />
        </svg>
      );
    case "CZ":
      return (
        <svg viewBox="0 0 12 9" width="100%" height="100%" aria-hidden>
          <rect width={12} height={4.5} fill="#FFFFFF" />
          <rect y={4.5} width={12} height={4.5} fill="#D7141A" />
          <path d="M0 0 L6 4.5 L0 9 Z" fill="#11457E" />
        </svg>
      );
    default:
      return null;
  }
}

/** Les pays que `Drapeau` sait dessiner — ce que `DrapeauDeLangue` consulte avant d'écrire des lettres. */
export const PAYS_DESSINES: ReadonlySet<string> = new Set([...Object.keys(BANDES), ...PAYS_SPECIAUX]);

export interface DrapeauProps {
  /** ISO 3166-1 alpha-2, n'importe quelle casse. */
  pays: string;
  /** Côté en px (le drapeau fait 4:3). Défaut 18. */
  taille?: number;
  className?: string;
}

export function Drapeau({ pays, taille = 18, className }: DrapeauProps) {
  const code = pays.toUpperCase();
  const bandes = BANDES[code];
  // 🪤 `<Special>` est un élément même quand il rend `null` : c'est la table qui dit s'il y a un
  // dessin, sinon les lettres ne s'écrivaient jamais (un pays inconnu rendait vide).
  const dessin = bandes ? <Bandes {...bandes} /> : PAYS_DESSINES.has(code) ? <Special pays={code} /> : null;
  return (
    <span
      className={cn(
        "ventes-drapeau inline-flex shrink-0 items-center justify-center overflow-hidden",
        "ring-2 ring-panel",
        !dessin && "ventes-etiquette bg-elevated text-[7px] font-medium uppercase tracking-wider text-muted",
        className,
      )}
      style={{ width: taille, height: Math.round(taille * 0.75) }}
      title={code}
      aria-label={code}
    >
      {dessin ?? code}
    </span>
  );
}
